import { must, supabase } from "./supabase";
import type { Stage } from "./stages";

// Every read and write goes through PostgREST as the signed-in founder, so the
// backend's RLS and triggers apply: owner defaults (set_sales_id_default),
// updated_at, the stage-change log (log_stage_change) and delete tombstones
// are all set server-side.

export type Option = { value: string; label: string };
export type Config = { title: string; companySectors: Option[] };

export type Sale = { id: number; first_name: string; last_name: string; email: string; disabled: boolean; user_id: string };

export type Company = {
  id: number; name: string; website: string | null; sector: string | null; description: string | null;
  stage: Stage; deal_value: number | null; sales_id: number | null; created_at: string | null; updated_at: string;
};

// People belong to companies; the pipeline (stage, deal value, owner) is the
// company's. contacts still has stage/deal_value/sales_id columns (the schema
// is unchanged); this app neither reads nor writes them.
export type Person = {
  id: number; first_name: string | null; last_name: string | null; title: string | null;
  email_jsonb: { email: string; type: string }[] | null; phone_jsonb: { number: string; type: string }[] | null;
  linkedin_url: string | null; company_id: number | null; background: string | null;
  created_at: string | null; updated_at: string;
};

export type StageChange = {
  id: number; entity_type: "company" | "contact"; entity_id: number; from_stage: Stage | null; to_stage: Stage;
  changed_at: string; changed_by: number | null;
};

const COMPANY = "id,name,website,sector,description,stage,deal_value,sales_id,created_at,updated_at";
const PERSON = "id,first_name,last_name,title,email_jsonb,phone_jsonb,linkedin_url,company_id,background,created_at,updated_at";
const CHANGE = "id,entity_type,entity_id,from_stage,to_stage,changed_at,changed_by";

// PostgREST returns numeric as a JSON number; normalise defensively in case a
// proxy or version returns it as a string.
const num = <T extends { deal_value: unknown }>(r: T): T => ({ ...r, deal_value: r.deal_value == null ? null : Number(r.deal_value) });

export async function loadConfig(): Promise<Config> {
  const row = must<{ config: Partial<Config> } | null>(await supabase.from("configuration").select("config").eq("id", 1).maybeSingle());
  return { title: row?.config?.title || "CRM", companySectors: row?.config?.companySectors ?? [] };
}

export async function listSales(): Promise<Sale[]> {
  return must(await supabase.from("sales").select("id,first_name,last_name,email,disabled,user_id").order("first_name"));
}

// ---- companies ----
export async function listCompanies(): Promise<Company[]> {
  return must<Company[]>(await supabase.from("companies").select(COMPANY).order("updated_at", { ascending: false })).map(num);
}
export type CompanyInput = Omit<Company, "id" | "created_at" | "updated_at">;
export async function createCompany(input: CompanyInput): Promise<Company> {
  return num(must<Company>(await supabase.from("companies").insert(input).select(COMPANY).single()));
}
export async function updateCompany(id: number, input: Partial<CompanyInput>): Promise<Company> {
  return num(must<Company>(await supabase.from("companies").update(input).eq("id", id).select(COMPANY).single()));
}

// ---- people (the contacts table) ----
export async function listPeople(): Promise<Person[]> {
  return must<Person[]>(await supabase.from("contacts").select(PERSON).order("updated_at", { ascending: false }));
}
export type PersonInput = Omit<Person, "id" | "created_at" | "updated_at">;
export async function createPerson(input: PersonInput): Promise<Person> {
  return must<Person>(await supabase.from("contacts").insert(input).select(PERSON).single());
}
export async function updatePerson(id: number, input: Partial<PersonInput>): Promise<Person> {
  return must<Person>(await supabase.from("contacts").update(input).eq("id", id).select(PERSON).single());
}

/** Move a company's card. The stage_changes row is written by the database trigger. */
export async function setCompanyStage(id: number, stage: Stage): Promise<void> {
  must(await supabase.from("companies").update({ stage }).eq("id", id));
}

// ---- stage log (read-only; companies only — people have no stage in this app) ----
export async function recentCompanyChanges(limit: number): Promise<StageChange[]> {
  return must(await supabase.from("stage_changes").select(CHANGE).eq("entity_type", "company").order("changed_at", { ascending: false }).order("id", { ascending: false }).limit(limit));
}
export async function companyStageHistory(id: number): Promise<StageChange[]> {
  return must(await supabase.from("stage_changes").select(CHANGE).eq("entity_type", "company").eq("entity_id", id).order("changed_at", { ascending: false }).order("id", { ascending: false }));
}

// ---- helpers ----
export function personName(p: Pick<Person, "first_name" | "last_name">): string {
  return `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Unnamed";
}
export function personEmail(p: Pick<Person, "email_jsonb">): string {
  return p.email_jsonb?.[0]?.email ?? "";
}
export function saleName(s: Sale | undefined): string {
  if (!s) return "Unassigned";
  return s.first_name && s.first_name !== "Pending" ? s.first_name : s.email;
}
export function optionLabel(options: Option[], value: string | null): string {
  if (!value) return "";
  return options.find((o) => o.value === value)?.label ?? value;
}
